import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Plus, Upload, FileText, Send, Download, Search, Eye, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useFileUpload, UploadProgressBar, UploadDetailedProgress } from "../lib/useFileUpload";

const STATUS_KEYS = {
  bozza: { k: "status_bozza", c: "bg-slate-200 text-slate-700" },
  inviato: { k: "status_inviato", c: "bg-blue-100 text-blue-800" },
  in_valutazione: { k: "status_in_valutazione", c: "bg-amber-100 text-amber-800" },
  valorizzato: { k: "status_valorizzato", c: "bg-green-100 text-green-800" },
  chiuso: { k: "status_chiuso", c: "bg-slate-300 text-slate-700" },
  abbandonato: { k: "status_abbandonato", c: "bg-rose-100 text-rose-800" },
};

const ABANDON_REASON_KEYS = [
  "reason_client_no",
  "reason_budget",
  "reason_other_supplier",
  "reason_timing",
  "reason_other",
];

export default function Projects() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language.startsWith("it") ? "it-IT" : "en-GB";
  const { user } = useAuth();
  const isTrader = user?.account_type === "trader";
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", client_name: "", description: "" });
  const [selected, setSelected] = useState(null);
  const [files, setFiles] = useState([]);
  const [abandonOpen, setAbandonOpen] = useState(null);
  const [abandonReason, setAbandonReason] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const reload = useCallback(async () => {
    const { data } = await api.get("/projects");
    setProjects(data);
  }, []);

  useEffect(() => { reload().catch(() => {}); }, [reload]);

  const filteredProjects = useMemo(() => {
    const k = query.trim().toLowerCase();
    return projects.filter((p) => {
      if (statusFilter !== "all" && p.status !== statusFilter) return false;
      if (!k) return true;
      return (
        (p.name || "").toLowerCase().includes(k) ||
        (p.client_name || "").toLowerCase().includes(k) ||
        (p.description || "").toLowerCase().includes(k)
      );
    });
  }, [projects, query, statusFilter]);

  async function loadFiles(pid) {
    const { data } = await api.get(`/projects/${pid}/files`);
    setFiles(data);
  }

  async function createProject(e) {
    e.preventDefault();
    try {
      await api.post("/projects", form);
      toast.success(t("projects.created_toast"));
      setForm({ name: "", client_name: "", description: "" });
      setOpen(false);
      reload();
    } catch { toast.error(t("projects.created_error")); }
  }

  async function uploadFileOne(pid, file, onProgress) {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", "generic");
    await api.post(`/projects/${pid}/files`, fd, {
      timeout: 0,
      onUploadProgress: (evt) => {
        if (onProgress) onProgress(
          evt.total ? Math.round((evt.loaded / evt.total) * 100) : 0,
          evt.loaded || 0,
          evt.total || file.size,
        );
      },
    });
  }

  const projectUpload = useFileUpload({
    uploadOne: useCallback(async (file, onProgress) => {
      if (!selected?.id) throw new Error("No project selected");
      await uploadFileOne(selected.id, file, onProgress);
    }, [selected?.id]),
    onAfter: useCallback(async () => {
      if (selected?.id) {
        await loadFiles(selected.id);
        await reload();
      }
    }, [selected?.id]),
    successLabel: "file",
    successLabelPlural: "files",
  });

  async function submitProject(pid) {
    try {
      await api.put(`/projects/${pid}/submit`);
      toast.success(t("projects.submit_success"));
      reload();
      setSelected(null);
    } catch { toast.error(t("projects.submit_error")); }
  }

  async function abandonProject(pid, reason) {
    try {
      await api.put(`/projects/${pid}/abandon`, { reason });
      toast.success(t("projects.abandon_success"));
      reload();
      setSelected(null);
      setAbandonOpen(null);
      setAbandonReason("");
    } catch { toast.error(t("projects.abandon_generic_error")); }
  }

  async function downloadFile(fid, filename) {
    try {
      const r = await api.get(`/files/${fid}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error(t("projects.download_error")); }
  }

  const [filePreview, setFilePreview] = useState(null);
  const [filePreviewLoading, setFilePreviewLoading] = useState(false);

  function isFilePreviewable(f) {
    const ct = (f.content_type || "").toLowerCase();
    const fn = (f.filename || "").toLowerCase();
    if (ct === "application/pdf" || fn.endsWith(".pdf")) return true;
    if (ct.startsWith("image/")) return true;
    return false;
  }

  async function previewFile(f) {
    if (!isFilePreviewable(f)) return;
    const ct = (f.content_type || "").toLowerCase();
    const isPdf = ct === "application/pdf" || (f.filename || "").toLowerCase().endsWith(".pdf");
    let pdfTab = null;
    if (isPdf) {
      pdfTab = window.open("about:blank", "_blank");
      if (!pdfTab) {
        toast.error(t("projects.preview_blocked"));
        return;
      }
    }
    setFilePreviewLoading(true);
    try {
      const r = await api.get(`/files/${f.id}/preview`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: f.content_type || "application/pdf" });
      const url = URL.createObjectURL(blob);
      if (isPdf && pdfTab) {
        pdfTab.location.href = url;
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else {
        setFilePreview({ url, name: f.filename, isPdf: false, isImage: ct.startsWith("image/") });
      }
    } catch {
      if (pdfTab) {
        try { pdfTab.close(); } catch (_) { /* ignore */ }
      }
      toast.error(t("projects.preview_unavailable"));
    }
    finally { setFilePreviewLoading(false); }
  }

  function closeFilePreview() {
    if (filePreview?.url) URL.revokeObjectURL(filePreview.url);
    setFilePreview(null);
  }

  return (
    <div className="pt-28 pb-20 bg-cream2 min-h-[100svh]" data-testid="projects-page">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-slate1">{t("projects.section")}</div>
            <h1 className="mt-2 font-display text-4xl lg:text-5xl font-light text-ink">{t("projects.title")}</h1>
            <p className="mt-3 text-slate1 max-w-2xl">{isTrader ? t("projects.subtitle_trader") : t("projects.subtitle_studio")}</p>
          </div>
          <button onClick={() => setOpen(true)} className="inline-flex items-center gap-2 bg-ink text-cream px-6 py-3 text-[12px] tracking-[0.2em] uppercase rounded-sm hover-lift" data-testid="new-project-btn">
            <Plus size={14} /> {t("projects.new")}
          </button>
        </div>

        {open && (
          <form onSubmit={createProject} className="mt-8 bg-white border border-ink/10 p-6 rounded-sm grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="project-form">
            <input required placeholder={t("projects.form_name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-ink/20 px-3 py-2 rounded-sm bg-cream" />
            <input placeholder={t("projects.form_client")} value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className="border border-ink/20 px-3 py-2 rounded-sm bg-cream" />
            <input placeholder={t("projects.form_desc")} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="border border-ink/20 px-3 py-2 rounded-sm bg-cream" />
            <div className="md:col-span-3 flex gap-3">
              <button className="bg-ink text-cream px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase rounded-sm">{t("projects.form_create")}</button>
              <button type="button" onClick={() => setOpen(false)} className="text-slate1 text-[11px] tracking-[0.2em] uppercase">{t("projects.form_cancel")}</button>
            </div>
          </form>
        )}

        <div className="mt-10 flex flex-col md:flex-row md:items-center gap-3" data-testid="projects-search">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate1" size={16} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("projects.search_placeholder")}
              className="w-full pl-10 pr-4 py-3 border border-ink/20 bg-white rounded-sm text-sm focus:outline-none focus:border-ink"
              data-testid="projects-search-input"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-ink/20 px-3 py-3 rounded-sm bg-white text-sm focus:outline-none focus:border-ink md:w-56"
            data-testid="projects-status-filter"
          >
            <option value="all">{t("projects.filter_all")}</option>
            {Object.entries(STATUS_KEYS).map(([k, v]) => (
              <option key={k} value={k}>{t(`projects.${v.k}`)}</option>
            ))}
          </select>
        </div>

        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="projects-list">
          {filteredProjects.length === 0 && (
            <div className="text-slate1 text-sm">
              {projects.length === 0 ? t("projects.no_projects") : t("projects.no_match")}
            </div>
          )}
          {filteredProjects.map((p) => {
            const s = STATUS_KEYS[p.status] || STATUS_KEYS.bozza;
            return (
              <div key={p.id} className="bg-white border border-ink/10 p-6 rounded-sm hover-lift" data-testid={`project-${p.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">{p.client_name || "—"}</div>
                    <div className="mt-1 font-display text-xl text-ink">{p.name}</div>
                    {p.description && <div className="mt-2 text-sm text-slate1 line-clamp-2">{p.description}</div>}
                  </div>
                  <span className={`px-3 py-1 text-[10px] tracking-[0.15em] uppercase rounded-sm ${s.c}`}>{t(`projects.${s.k}`)}</span>
                </div>

                <div className={`mt-5 grid ${isTrader ? "grid-cols-2" : "grid-cols-3"} gap-4 text-sm`}>
                  <div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">{t("projects.files_lbl")}</div>
                    <div className="text-ink font-medium">{p.files?.length || 0}</div>
                  </div>
                  <div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">{t("projects.value_lbl")}</div>
                    <div className="text-ink font-medium">{p.value > 0 ? `€ ${p.value.toLocaleString("it-IT")}` : "—"}</div>
                  </div>
                  {!isTrader && (
                    <div>
                      <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">{t("projects.credit_lbl")}</div>
                      <div className="text-success font-medium">+ € {(p.credit_earned || 0).toFixed(2)}</div>
                    </div>
                  )}
                </div>

                <div className="mt-5 flex gap-2 flex-wrap">
                  <button onClick={() => { setSelected(p); loadFiles(p.id); }} className="text-[11px] tracking-[0.2em] uppercase text-ink underline" data-testid={`open-${p.id}`}>{t("projects.open")}</button>
                  {p.status === "bozza" && (p.files?.length || 0) > 0 && (
                    <button onClick={() => submitProject(p.id)} className="ml-auto inline-flex items-center gap-1 text-[11px] tracking-[0.2em] uppercase text-ink hover:text-gold" data-testid={`submit-${p.id}`}>
                      <Send size={12} /> {t("projects.submit")}
                    </button>
                  )}
                  {!["valorizzato", "chiuso", "abbandonato"].includes(p.status) && (
                    <button onClick={() => { setAbandonOpen(p.id); setAbandonReason(""); }} className={`${p.status === "bozza" ? "" : "ml-auto"} text-[11px] tracking-[0.2em] uppercase text-rose-700 hover:text-rose-900`} data-testid={`abandon-${p.id}`}>
                      {t("projects.abandon")}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Abandon modal */}
        {abandonOpen && (
          <div className="fixed inset-0 z-[60] bg-ink/60 flex items-center justify-center p-6" onClick={() => setAbandonOpen(null)}>
            <div className="bg-cream max-w-md w-full p-8 rounded-sm" onClick={(e) => e.stopPropagation()} data-testid="abandon-modal">
              <div className="text-[11px] tracking-[0.3em] uppercase text-rose-700">{t("projects.abandon_title")}</div>
              <div className="mt-2 font-display text-2xl text-ink">{t("projects.abandon_pick_reason")}</div>
              <p className="mt-2 text-sm text-slate1">{t("projects.abandon_desc")}</p>
              <div className="mt-5 space-y-2">
                {ABANDON_REASON_KEYS.map(k => {
                  const label = t(`projects.${k}`);
                  return (
                    <label key={k} className="flex items-center gap-3 cursor-pointer p-3 border border-ink/15 rounded-sm hover:bg-white">
                      <input type="radio" name="reason" value={label} checked={abandonReason === label} onChange={(e) => setAbandonReason(e.target.value)} data-testid={`reason-${k}`} />
                      <span className="text-sm text-ink">{label}</span>
                    </label>
                  );
                })}
              </div>
              <div className="mt-6 flex gap-3 justify-end">
                <button onClick={() => setAbandonOpen(null)} className="text-[11px] tracking-[0.2em] uppercase text-slate1">{t("projects.form_cancel")}</button>
                <button onClick={() => abandonReason && abandonProject(abandonOpen, abandonReason)} disabled={!abandonReason} className="bg-rose-700 text-cream px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase rounded-sm disabled:opacity-50" data-testid="abandon-confirm">
                  {t("projects.abandon_confirm")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Side panel */}
        {selected && (
          <div className="fixed inset-0 z-50 bg-ink/40" onClick={() => setSelected(null)}>
            <div className="absolute right-0 top-0 bottom-0 w-full max-w-xl bg-cream overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="project-detail">
              <div className="p-6 border-b border-ink/10 flex items-center justify-between">
                <div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">{selected.client_name || t("projects.new")}</div>
                  <div className="font-display text-xl text-ink">{selected.name}</div>
                </div>
                <button onClick={() => setSelected(null)} className="text-slate1 text-sm">{t("projects.panel_close")}</button>
              </div>
              <div className="p-6">
                {isTrader && selected.delivery_date && (
                  <div className="mb-5 p-4 bg-ink text-cream rounded-sm flex items-center justify-between" data-testid="trader-delivery">
                    <div>
                      <div className="text-[10px] tracking-[0.2em] uppercase text-cream/60">{t("projects.delivery_date_lbl")}</div>
                      <div className="font-display text-xl mt-1">{new Date(selected.delivery_date).toLocaleDateString(dateLocale, { day: "2-digit", month: "long", year: "numeric" })}</div>
                    </div>
                  </div>
                )}

                <label className="block text-[11px] tracking-[0.2em] uppercase text-slate1 mb-2">{t("projects.upload_files_lbl")}</label>
                <div
                  className={`border-2 border-dashed ${projectUpload.dragOver ? "border-ink bg-cream2" : "border-ink/20 bg-white"} p-4 rounded-sm transition-colors text-center cursor-pointer hover:bg-cream2`}
                  {...projectUpload.handlers}
                  onClick={(e) => {
                    if (projectUpload.uploading) return;
                    const inp = e.currentTarget.querySelector('input[type="file"]');
                    if (inp) inp.click();
                  }}
                  data-testid="project-upload-dropzone"
                >
                  <Upload size={16} className="inline mr-2 text-slate1" />
                  <span className="text-[11px] tracking-[0.2em] uppercase text-slate1">
                    {projectUpload.uploading
                      ? (projectUpload.progress ? t("projects.dropzone_uploading_x_of_y", { done: projectUpload.progress.done, total: projectUpload.progress.total }) : t("projects.dropzone_uploading"))
                      : (projectUpload.dragOver ? t("projects.dropzone_drop_here") : t("projects.dropzone_drag_or_click"))}
                  </span>
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,.dwg,.zip" className="hidden" {...projectUpload.inputProps} data-testid="file-input" />
                </div>
                <UploadProgressBar progress={projectUpload.progress} />
                <UploadDetailedProgress perFile={projectUpload.perFile} />

                {isTrader && (() => {
                  const proforma = files.filter((f) => f.category === "proforma");
                  const conferme = files.filter((f) => f.category === "conferma_ordine");
                  if (proforma.length === 0 && conferme.length === 0) return null;
                  const groups = [
                    { key: "proforma", label: t("projects.cat_proforma"), items: proforma },
                    { key: "conferma_ordine", label: t("projects.cat_conferme"), items: conferme },
                  ];
                  return (
                    <div className="mt-6 space-y-4">
                      {groups.filter((g) => g.items.length > 0).map((g) => (
                        <div key={g.key} className="bg-white border border-gold/30 rounded-sm" data-testid={`trader-cat-${g.key}`}>
                          <div className="px-4 py-2.5 border-b border-ink/10 bg-gold/5 text-[10px] tracking-[0.2em] uppercase text-gold">
                            {t("projects.cat_count_label", { label: g.label, count: g.items.length })}
                          </div>
                          <div className="divide-y divide-ink/10">
                            {g.items.map((f) => (
                              <div key={f.id} className="p-3 flex items-center gap-3">
                                <FileText size={16} className="text-gold" />
                                <div className="flex-1 min-w-0">
                                  <div className="text-ink text-sm truncate">{f.filename}</div>
                                  <div className="text-xs text-slate1">{(f.size / 1024).toFixed(0)} KB · {new Date(f.created_at).toLocaleDateString(dateLocale)}</div>
                                </div>
                                <button onClick={() => downloadFile(f.id, f.filename)} className="text-slate1 hover:text-ink"><Download size={16} /></button>
                                {isFilePreviewable(f) && (
                                  <button onClick={() => previewFile(f)} disabled={filePreviewLoading} className="text-slate1 hover:text-ink disabled:opacity-50" aria-label={t("projects.preview_aria")}><Eye size={16} /></button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}

                {(() => {
                  const sentFiles = files.filter(f => f.uploader_role !== "admin" && (!f.category || f.category === "generic"));
                  const receivedFiles = files.filter(f => f.uploader_role === "admin" && (!f.category || f.category === "generic"));
                  return (
                    <div className="mt-6">
                      <div className="text-[10px] tracking-[0.2em] uppercase text-slate1 mb-3">{t("projects.files_sent", { count: sentFiles.length })}</div>
                      <div className="divide-y divide-ink/10 bg-white border border-ink/10 rounded-sm">
                        {sentFiles.length === 0 && <div className="p-4 text-sm text-slate1">{t("projects.no_files_sent")}</div>}
                        {sentFiles.map((f) => (
                          <div key={f.id} className="p-3 flex items-center gap-3" data-testid={`file-sent-${f.id}`}>
                            <FileText size={16} className="text-slate1" />
                            <div className="flex-1 min-w-0">
                              <div className="text-ink text-sm truncate">{f.filename}</div>
                              <div className="text-xs text-slate1">{(f.size / 1024).toFixed(0)} KB · {f.uploader_name || t("projects.you")} · {new Date(f.created_at).toLocaleDateString(dateLocale)}</div>
                            </div>
                            <button onClick={() => downloadFile(f.id, f.filename)} className="text-slate1 hover:text-ink" data-testid={`dl-${f.id}`}><Download size={16} /></button>
                            {isFilePreviewable(f) && (
                              <button onClick={() => previewFile(f)} disabled={filePreviewLoading} className="text-slate1 hover:text-ink disabled:opacity-50" data-testid={`prev-${f.id}`} aria-label={t("projects.preview_aria")}><Eye size={16} /></button>
                            )}
                          </div>
                        ))}
                      </div>

                      <div className="text-[10px] tracking-[0.2em] uppercase text-slate1 mt-6 mb-3">{t("projects.files_received", { count: receivedFiles.length })}</div>
                      <div className="divide-y divide-ink/10 bg-white border border-ink/10 rounded-sm">
                        {receivedFiles.length === 0 && <div className="p-4 text-sm text-slate1">{t("projects.no_files_received")}</div>}
                        {receivedFiles.map((f) => (
                          <div key={f.id} className="p-3 flex items-center gap-3" data-testid={`file-received-${f.id}`}>
                            <FileText size={16} className="text-gold" />
                            <div className="flex-1 min-w-0">
                              <div className="text-ink text-sm truncate">{f.filename}</div>
                              <div className="text-xs text-slate1">{(f.size / 1024).toFixed(0)} KB · {f.uploader_name || t("projects.archi_hub")} · {new Date(f.created_at).toLocaleDateString(dateLocale)}</div>
                            </div>
                            <button onClick={() => downloadFile(f.id, f.filename)} className="text-slate1 hover:text-ink"><Download size={16} /></button>
                            {isFilePreviewable(f) && (
                              <button onClick={() => previewFile(f)} disabled={filePreviewLoading} className="text-slate1 hover:text-ink disabled:opacity-50" data-testid={`prev-recv-${f.id}`} aria-label={t("projects.preview_aria")}><Eye size={16} /></button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {selected.value > 0 && (
                  <div className="mt-6 p-5 bg-ink text-cream rounded-sm">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-cream/60">{t("projects.project_value_lbl")}</div>
                    <div className="font-display text-3xl mt-1">€ {selected.value.toLocaleString("it-IT")}</div>
                    {!isTrader && (
                      <div className="text-sm text-success mt-2">{t("projects.wallet_credit_added", { amount: selected.credit_earned.toFixed(2) })}</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {filePreview && (
        <div className="fixed inset-0 z-[100] bg-ink/85 backdrop-blur-sm flex flex-col" onClick={closeFilePreview} data-testid="file-preview-modal">
          <div className="flex items-center justify-between px-6 py-3 bg-ink text-cream" onClick={(e) => e.stopPropagation()}>
            <div className="text-sm tracking-[0.15em] uppercase truncate">{filePreview.name}</div>
            <button onClick={closeFilePreview} className="w-9 h-9 flex items-center justify-center hover:bg-cream/10 rounded-sm" data-testid="file-preview-close" aria-label={t("projects.preview_close")}>
              <X size={18} />
            </button>
          </div>
          <div className="flex-1 bg-cream2" onClick={(e) => e.stopPropagation()}>
            {filePreview.isPdf ? (
              <iframe src={filePreview.url} title={filePreview.name} className="w-full h-full border-0" data-testid="file-preview-iframe" />
            ) : filePreview.isImage ? (
              <div className="w-full h-full flex items-center justify-center p-4">
                <img src={filePreview.url} alt={filePreview.name} className="max-w-full max-h-full object-contain" />
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate1 text-sm">{t("projects.preview_unsupported")}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
